/**
 * RTK Query custom base query for odl_app endpoints.
 *
 * Wraps the existing axios apiClient so all auth headers, token refresh
 * interceptors, and socket-ID injection continue to work. Builds grid query
 * params from a normalized { sort, filters, search, page } arg and passes
 * the { data, meta } response body straight through to endpoint
 * transformResponse handlers.
 */

import { buildGridParams } from '@sophocles/data-grids/datasource/params';
// eslint-disable-next-line no-restricted-imports -- this IS the entities-layer api module, just not named api.js
import apiClient from '@sophocles/http-common';

/**
 * Factory that returns an RTK Query baseQuery function backed by apiClient.
 *
 * Each endpoint arg may include:
 *   url     {string}  Required. Relative path (no leading slash needed).
 *   method  {string}  HTTP verb, default 'get'.
 *   data    {any}     Request body for POST / PATCH.
 *   grid    {object}  Normalized grid descriptor → converted to query params
 *                     via buildGridParams. Mutually exclusive with direct params.
 *   params  {object}  Query params for non-grid requests.
 *
 * On success returns { data: res.data } where res.data is the raw
 * { data, meta } envelope; endpoints pick what they need via transformResponse.
 *
 * On failure returns { error: { status, data } } matching the RTK Query error
 * shape, carrying the HTTP status code and the response body (e.g. { detail }).
 *
 * @returns {function} RTK Query baseQuery
 */
export const axiosBaseQuery =
  () =>
  async ({ url, method = 'get', data, grid, params } = {}) => {
    try {
      // All entities-client resources live under /api/odl-app/. apiClient's base
      // is /api, so prepend the odl-app segment (endpoints pass bare resource
      // paths like "sites/1/cameras/grid-spec/").
      const path = String(url ?? '').replace(/^\/+/, '');
      const fullUrl = path.startsWith('odl-app/') ? path : `odl-app/${path}`;
      const res = await apiClient.request({
        url: fullUrl,
        method,
        data,
        params: grid ? buildGridParams(grid) : params,
      });
      // Pass the full { data, meta } envelope; transformResponse in each
      // endpoint unwraps only what it needs.
      return { data: res.data };
    } catch (err) {
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data,
        },
      };
    }
  };
